/**
 * Google Analytics 4.
 *
 * The script is injected only after the visitor has consented to analytics, and
 * removed again when they withdraw. Nothing about GA — no script tag, no
 * cookie, no request — happens before that, which is what Art. 6(1)(a) GDPR
 * and § 25(1) TDDDG require for a non-essential tracker.
 *
 * Set REACT_APP_GA_MEASUREMENT_ID (e.g. G-XXXXXXXXXX) in the Netlify
 * environment. Without it, everything here is a no-op and the site simply runs
 * without analytics.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    [key: `ga-disable-${string}`]: boolean | undefined;
  }
}

export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

const SCRIPT_ID = 'ga4-script';

export const isAnalyticsConfigured = (): boolean => Boolean(GA_MEASUREMENT_ID);

/** Injects gtag.js and configures it. Safe to call repeatedly. */
export const initGA = () => {
  if (!GA_MEASUREMENT_ID || typeof window === 'undefined') return;
  if (document.getElementById(SCRIPT_ID)) return;

  window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    // Page views are sent by GoogleAnalytics.tsx on route changes instead, so
    // that client-side navigation is counted exactly once.
    send_page_view: false,
    anonymize_ip: true,
    // Keep GA from setting advertising cookies or sharing for ad purposes.
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
};

/**
 * Withdrawing consent has to stop collection immediately and clear what was
 * already set — removing the script alone would leave the cookies behind.
 */
export const disableGA = () => {
  if (!GA_MEASUREMENT_ID || typeof window === 'undefined') return;

  window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;

  document.getElementById(SCRIPT_ID)?.remove();

  // _ga and _ga_<container> are GA4's own cookies.
  document.cookie.split(';').forEach((entry) => {
    const name = entry.split('=')[0]?.trim();
    if (!name || !name.startsWith('_ga')) return;
    const domain = window.location.hostname;
    document.cookie = `${name}=; Max-Age=0; path=/`;
    document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}`;
    document.cookie = `${name}=; Max-Age=0; path=/; domain=.${domain}`;
  });
};

export const trackPageView = (path: string, title?: string) => {
  window.gtag?.('event', 'page_view', { page_path: path, page_title: title });
};

export const trackEvent = (name: string, params?: Record<string, unknown>) => {
  window.gtag?.('event', name, params);
};
